'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { formatMoney } from '@/lib/money';

import {
  getPriceColumnRole,
  type BulkEditStoreCurrencies,
} from '../bulk-edit-currencies';
import type { BulkEditRow, BulkEditRowEdits } from '../bulk-edit.types';
import {
  formatPriceDisplay,
  getRowCurrencyAmount,
  isCellDirty,
  parsePriceInput,
} from '../bulk-edit.utils';
import { BulkEditEmptyDash, BulkEditTextInput } from './bulk-edit-field-cell';

interface BulkEditPriceCellProps {
  row: BulkEditRow;
  edits: Map<string, BulkEditRowEdits>;
  storeCurrencies: BulkEditStoreCurrencies;
  variantDisabled: boolean;
  priceLocked: boolean;
  onUpdate: (patch: Record<string, unknown>) => void;
}

function isPriceCellDirty(rowKey: string, edits: Map<string, BulkEditRowEdits>) {
  const rowEdits = edits.get(rowKey);
  return rowEdits?.currencyEdits != null && Object.keys(rowEdits.currencyEdits).length > 0;
}

function BulkEditPriceInput({
  currency,
  value,
  onChange,
  disabled,
  title,
}: {
  currency: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <BulkEditTextInput
      type="number"
      step="0.01"
      min={0}
      align="right"
      value={value}
      onChange={onChange}
      disabled={disabled}
      title={title}
    />
  );
}

export function BulkEditPriceCell({
  row,
  edits,
  storeCurrencies,
  variantDisabled,
  priceLocked,
  onUpdate,
}: BulkEditPriceCellProps) {
  const { t } = useTranslation(['products']);
  const currencies = storeCurrencies.allPriceCurrencies;
  const primaryCurrency =
    storeCurrencies.chargeCurrencies[0] ?? storeCurrencies.baseCurrency;
  const dirty = isPriceCellDirty(row.key, edits);

  if (variantDisabled) {
    return <BulkEditEmptyDash align="right" />;
  }

  const lockedTitle = t('products:bulkEditor.priceCell.externalSellingPlan');

  if (currencies.length <= 1) {
    const currency = currencies[0] ?? primaryCurrency;
    return (
      <BulkEditPriceInput
        currency={currency}
        value={String(formatPriceDisplay(getRowCurrencyAmount(row, currency, edits), currency) ?? '')}
        onChange={(v) =>
          onUpdate({
            currencyEdits: { [currency]: parsePriceInput(v, currency) },
          })
        }
        disabled={priceLocked}
        title={priceLocked ? lockedTitle : undefined}
      />
    );
  }

  const displayCurrencies = storeCurrencies.presentmentCurrencies.filter(
    (c) => getPriceColumnRole(c, storeCurrencies) === 'display',
  );
  const chargeCurrencies = storeCurrencies.chargeCurrencies.filter(
    (c) => getPriceColumnRole(c, storeCurrencies) === 'charge',
  );
  const bothCurrencies = currencies.filter(
    (c) => getPriceColumnRole(c, storeCurrencies) === 'both',
  );

  const primaryAmount = getRowCurrencyAmount(row, primaryCurrency, edits);
  const extraCount = currencies.length - 1;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'crm-bulk-edit-price-trigger',
            dirty && 'crm-bulk-edit-price-trigger--dirty',
          )}
        >
          <span className="crm-bulk-edit-price-trigger__amount">
            {primaryAmount != null
              ? formatMoney(primaryAmount, primaryCurrency)
              : t('products:bulkEditor.priceCell.setPrices')}
          </span>
          {extraCount > 0 ? (
            <Badge variant="secondary" className="crm-bulk-edit-price-trigger__badge">
              +{extraCount}
            </Badge>
          ) : null}
          <ChevronDown className="crm-bulk-edit-price-trigger__chevron" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="crm-bulk-edit-price-popover w-80 p-0">
        <Command className="crm-bulk-edit-price-command">
          <CommandInput placeholder={t('products:bulkEditor.priceCell.searchCurrency')} />
          <CommandList className="max-h-72">
            <CommandEmpty>{t('products:bulkEditor.priceCell.noCurrency')}</CommandEmpty>

            {bothCurrencies.length > 0 ? (
              <CurrencyPriceGroup
                row={row}
                edits={edits}
                currencies={bothCurrencies}
                storeCurrencies={storeCurrencies}
                heading={t('products:bulkEditor.priceCell.groupBoth')}
                onUpdate={onUpdate}
                locked={priceLocked}
                lockedTitle={lockedTitle}
              />
            ) : null}

            {chargeCurrencies.length > 0 ? (
              <CurrencyPriceGroup
                row={row}
                edits={edits}
                currencies={chargeCurrencies}
                storeCurrencies={storeCurrencies}
                heading={t('products:bulkEditor.priceCell.groupCharge')}
                onUpdate={onUpdate}
                locked={priceLocked}
                lockedTitle={lockedTitle}
              />
            ) : null}

            {displayCurrencies.length > 0 ? (
              <CurrencyPriceGroup
                row={row}
                edits={edits}
                currencies={displayCurrencies}
                storeCurrencies={storeCurrencies}
                heading={t('products:bulkEditor.priceCell.groupDisplay')}
                onUpdate={onUpdate}
                locked={priceLocked}
                lockedTitle={lockedTitle}
              />
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function CurrencyPriceGroup({
  row,
  edits,
  currencies,
  storeCurrencies,
  heading,
  onUpdate,
  locked,
  lockedTitle,
}: {
  row: BulkEditRow;
  edits: Map<string, BulkEditRowEdits>;
  currencies: string[];
  storeCurrencies: BulkEditStoreCurrencies;
  heading: string;
  onUpdate: (patch: Record<string, unknown>) => void;
  locked: boolean;
  lockedTitle: string;
}) {
  const { t } = useTranslation(['products']);

  return (
    <CommandGroup heading={heading}>
      {currencies.map((currency) => {
        const role = getPriceColumnRole(currency, storeCurrencies);
        const roleLabel =
          role === 'display'
            ? t('products:bulkEditor.priceCell.roleDisplay')
            : role === 'charge'
              ? t('products:bulkEditor.priceCell.roleCharge')
              : null;

        return (
          <CommandItem
            key={currency}
            value={`${currency} ${heading}`}
            className="crm-bulk-edit-price-row"
            onSelect={() => {}}
          >
            <div className="crm-bulk-edit-price-row__code">
              <Badge variant="outline" className="text-[10px] font-medium">
                {currency}
              </Badge>
              {roleLabel ? (
                <span className="text-[10px] text-muted-foreground">{roleLabel}</span>
              ) : null}
            </div>
            <div
              className="crm-bulk-edit-price-row__input"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <BulkEditPriceInput
                currency={currency}
                value={String(formatPriceDisplay(getRowCurrencyAmount(row, currency, edits), currency) ?? '')}
                onChange={(v) =>
                  onUpdate({
                    currencyEdits: { [currency]: parsePriceInput(v, currency) },
                  })
                }
                disabled={locked}
                title={locked ? lockedTitle : undefined}
              />
            </div>
          </CommandItem>
        );
      })}
    </CommandGroup>
  );
}

export { isPriceCellDirty };
